import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Tooltip } from 'antd';
import { ReactComponent as ArrowRightSvg } from '@assets/icons/arrow-right-without-line.svg';
import { ReactComponent as ArrowLeftSvg } from '@assets/icons/arrow-left-without-line.svg';
import { ReactComponent as ArrowDownSvg } from '@assets/icons/arrow-down.svg';
import { ReactComponent as CheckMarkBlueSvg } from '@assets/icons/check-mark-blue.svg';
import { ReactComponent as ArrowCheckSvg } from '@assets/icons/check-task.svg';
import { ReactComponent as LockSvg } from '@assets/icons/lock.svg';
import { useResponsive } from '@hooks/useResponsive';
import { useGetApprovalWorkflow } from '@hooks/queries/approval';
import { ApprovalWorkflowItemDto, AssetItemDto, AWAssetDto } from '@api/Api';
import { useTypedSelector } from '@hooks';
import { useMediaViewerMobileActionVisibility } from '@pages/MediaViewer/components/MediaViewerMobileAction/MediaViewerMobileAction';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import { sortedAWListAssetsToReviewInBatch } from '@components/Modals/ApprovalModal/utils';
import { useShareReview } from '@components/ShareReviewProvider';
import { useApprovalPassword } from '@context/ApprovalPasswordContext';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { nonNull } from '@helpers/non-null';
import { setAsset, setAssetVariant } from '@redux/actions/mediaViewerAction';
import useOnboardingProcess from '@hooks/useOnboardingProcess';

import './AssetsSlider.less';

type AssetsSliderItemProps = {
  asset: AWAssetDto | AssetItemDto;
  isActive: boolean;
  approvalWorkflow?: ApprovalWorkflowItemDto;
  onClick: () => void;
};

function isApprovalAsset(
  asset: AWAssetDto | AssetItemDto
): asset is AWAssetDto {
  return 'decision' in asset;
}

function AssetsSliderItem({
  asset,
  isActive,
  approvalWorkflow,
  onClick
}: AssetsSliderItemProps) {
  const lockDecisions = !!approvalWorkflow?.lockDecisions;
  const decision = isApprovalAsset(asset) ? asset.decision : null;
  const accessDisabled = isApprovalAsset(asset) ? asset.accessDisabled : false;

  const disableReason =
    lockDecisions && !!decision ? (
      <>
        Your decision is locked.
        <br />
        Contact the approval manager
        <br />
        if you need to change it.
      </>
    ) : accessDisabled ? (
      <>
        Your access to this media is restricted.
        <br />
        Contact the approval manager
        <br />
        if you need to request access.
      </>
    ) : null;

  const showLock = !!disableReason;
  const grayOut = !!disableReason || !!decision;

  const previewUrl = isApprovalAsset(asset)
    ? asset.previewUrl
    : asset.asset?.versions[0]?.metadata.preview?.url;
  const assetType = isApprovalAsset(asset)
    ? asset.type
    : asset.asset?.versions[0]?.metadata.type;

  const preparePreview = useMemo(() => {
    const res = assetPreviewThumbnails({
      url: previewUrl,
      status: 'ready',
      type: assetType || 'unknown',
      format: 'img'
    });

    if (typeof res === 'string')
      return <img draggable={false} src={res || ''} alt="" />;
    return res;
  }, [previewUrl, assetType]);

  const name = isApprovalAsset(asset)
    ? asset.name
    : asset.asset?.versions[0]?.name;
  const extension = isApprovalAsset(asset)
    ? asset.extension
    : asset.asset?.versions[0]?.extension;
  const versionNumber = isApprovalAsset(asset)
    ? asset.versionsCount
    : asset.asset?.versions[0]?.versionNumber;

  return (
    <Tooltip
      overlayClassName="assets-slider_tooltip"
      title={
        <>
          {!disableReason && (
            <div className="assets-slider_tooltip-head">
              <div className="assets-slider_tooltip-name">
                {name}
                <span className="assets-slider_tooltip-extension">
                  .{extension}
                </span>
              </div>
              <div className="assets-slider_tooltip-version">
                V{versionNumber}
              </div>
            </div>
          )}
          {disableReason && (
            <div className="assets-slider_tooltip-body">{disableReason}</div>
          )}
        </>
      }
    >
      <div
        className={classNames('assets-slider_item', {
          'assets-slider_item--active': isActive,
          'assets-slider_item--disabled': grayOut,
          'assets-slider_item--blur': accessDisabled
        })}
        onClick={accessDisabled ? undefined : onClick}
        style={{ cursor: accessDisabled ? 'not-allowed' : undefined }}
      >
        {showLock && (
          <div className="assets-slider_item-lock-icon">
            <LockSvg />
          </div>
        )}
        {preparePreview}
        {!!decision && (
          <div
            className={classNames([
              'assets-slider_item-icon',
              `b-color-${decision}`
            ])}
          >
            <ArrowCheckSvg />
          </div>
        )}
      </div>
    </Tooltip>
  );
}

export default function AssetsSlider() {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const { isMdWidth } = useResponsive();
  const location = useLocation();
  const shareReview = useShareReview(true);
  const { password } = useApprovalPassword();
  const [searchParams] = useSearchParams();
  const { tourData, goToStep } = useOnboardingProcess();
  const selectedAsset = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.assets
  );
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const canShowFloatComments = !!selectedAsset && !!selectedVersion;
  const mobileVisibility = useMediaViewerMobileActionVisibility({
    isShowFloatCommentForm: canShowFloatComments,
    isShowFloatCommentItem: canShowFloatComments
  });
  const isAnnouncementMediaViewerMobileTour =
    tourData?.type === 'announcement-media-viewer-mobile';
  const isAnnouncementMediaViewerCarouselTour =
    isAnnouncementMediaViewerMobileTour && goToStep === 0;
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  const isInternalMediaViewer = location.pathname.includes('media-viewer');
  const awId =
    searchParams.get('id') ?? selectedAsset?.asset?.approvalWorkflow?.id;
  const selectedAssetId = selectedAsset?.id;
  const contentRef = useRef<HTMLDivElement>(null);
  const { data: resApprovalWorkflow } = useGetApprovalWorkflow({
    params: {
      id: awId || '',
      password
    }
  });
  const aw = resApprovalWorkflow?.approvalWorkflow;

  const sortedAssets = useMemo(() => {
    if (shareReview?.content?.assets) return shareReview.content.assets;
    if (!aw?.assets) return [];
    if (aw.isPaused) return [];
    return sortedAWListAssetsToReviewInBatch(aw.assets);
  }, [shareReview?.content?.assets, aw?.isPaused, aw?.assets]);

  const count = sortedAssets?.length || 0;

  const currentStage = useMemo(() => {
    if (!aw?.stages?.length) return null;
    return (
      aw.stages.find((stage) => stage.id === aw.currentStageId) || aw.stages[0]
    );
  }, [aw?.stages, aw?.currentStageId]);

  const currentMember = useMemo(
    () => currentStage?.members.find((member) => member.me),
    [currentStage?.members]
  );

  const { reviewCount, toReviewCount, canReview, isReviewed } = useMemo(() => {
    if (!currentMember?.canSubmitDecision) {
      return {
        reviewCount: 0,
        toReviewCount: 0,
        canReview: false,
        isReviewed: false
      };
    }

    const reviewableAssets =
      aw?.assets?.filter((asset) => !asset.accessDisabled) || [];
    const toReviewCount = reviewableAssets.length;
    const reviewCount = reviewableAssets.filter((asset) =>
      currentMember.decisions?.some(
        (x) => x.assetVersionId === asset.versionId && x.decision
      )
    ).length;

    return {
      reviewCount,
      toReviewCount,
      canReview: toReviewCount > 0,
      isReviewed: toReviewCount > 0 && reviewCount === toReviewCount
    };
  }, [aw?.assets, currentMember]);

  const reviewPositionLabel = canReview
    ? `${reviewCount} / ${toReviewCount}`
    : null;

  const hideMobileReviewBanner = !!mobileVisibility?.showFloatCommentItem;

  useEffect(() => {
    if (hideMobileReviewBanner) {
      setIsMobileExpanded(false);
    }
  }, [hideMobileReviewBanner]);

  const onChangeAsset = (nextAsset: AWAssetDto | AssetItemDto) => {
    if (!nextAsset) return;
    if (isApprovalAsset(nextAsset)) {
      if (nextAsset.accessDisabled) return;
      if (isInternalMediaViewer && nextAsset.hasAccess) {
        navigate(`/media-viewer/${nextAsset.id}/${nextAsset.versionId}`, {
          replace: true
        });
        return;
      }
      if (isInternalMediaViewer && !nextAsset.hasAccess) {
        navigate(`/approval?id=${awId}&assetId=${nextAsset.id}`, {
          replace: true
        });
        return;
      }
      searchParams.set('assetId', nextAsset.id);
      searchParams.delete('versionId');
      navigate({ search: searchParams.toString() }, { replace: true });
    } else {
      dispatch(setAsset(nextAsset));
      dispatch(setAssetVariant(nonNull(nextAsset.asset).versions[0].id));
    }
  };

  const onPrev = () => {
    const assets = [...sortedAssets].reverse();
    const currentIndex = assets.findIndex(
      (item) => item.id === selectedAssetId
    );
    let prevAsset = assets.find(
      (x, i) => i > currentIndex && (!isApprovalAsset(x) || !x.accessDisabled)
    );
    if (!prevAsset) {
      prevAsset = assets.find(
        (x, i) => i < currentIndex && (!isApprovalAsset(x) || !x.accessDisabled)
      );
    }
    if (prevAsset) onChangeAsset(prevAsset);
  };

  const onNext = () => {
    const currentIndex = sortedAssets.findIndex(
      (item) => item.id === selectedAssetId
    );
    let nextAsset = sortedAssets.find(
      (x, i) => i > currentIndex && (!isApprovalAsset(x) || !x.accessDisabled)
    );
    if (!nextAsset) {
      nextAsset = sortedAssets.find(
        (x, i) => i < currentIndex && (!isApprovalAsset(x) || !x.accessDisabled)
      );
    }
    if (nextAsset) onChangeAsset(nextAsset);
  };

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    if (!sortedAssets.length) return;
    const currentIndex = sortedAssets.findIndex(
      (item) => item.id === selectedAssetId
    );
    const itemElement = content.children[currentIndex];
    if (itemElement) {
      itemElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start'
      });
    }
  }, [sortedAssets, selectedAssetId, isMobileExpanded]);

  if (count <= 1 && !(isMdWidth && isAnnouncementMediaViewerMobileTour)) {
    return null;
  }

  const sliderItems = sortedAssets.map((item) => {
    const isActive = selectedAssetId === item.id;
    return (
      <AssetsSliderItem
        asset={item}
        approvalWorkflow={aw}
        isActive={isActive}
        onClick={() => {
          if (isActive) return;
          onChangeAsset(item);
        }}
        key={item.id}
      />
    );
  });

  if (isMdWidth) {
    if (hideMobileReviewBanner) {
      return null;
    }

    return (
      <div
        className={classNames('assets-slider', {
          'assets-slider--mobile-expanded': isMobileExpanded,
          'assets-slider--mobile-collapsed': !isMobileExpanded
        })}
      >
        <div
          className={classNames('assets-slider_mobile-expandable', {
            'assets-slider_mobile-expandable--expanded': isMobileExpanded
          })}
        >
          <div className="assets-slider_mobile-expandable_section">
            <div
              className={classNames(
                'assets-slider_mobile',
                'tour-announcement-media-viewer-carousel',
                {
                  'tour-announcement-media-viewer-carousel-active':
                    isAnnouncementMediaViewerCarouselTour
                }
              )}
              onClick={() => setIsMobileExpanded(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsMobileExpanded(true);
                }
              }}
            >
              <div
                className={classNames('assets-slider_mobile-icon', {
                  'assets-slider_mobile-icon--reviewed': isReviewed
                })}
              >
                <CheckMarkBlueSvg width={20} height={20} />
              </div>
              <div className="assets-slider_mobile-label">
                Review{reviewPositionLabel ? `: ${reviewPositionLabel}` : ''}
              </div>
              <ArrowDownSvg className="assets-slider_mobile-chevron" />
            </div>
          </div>
          <div className="assets-slider_mobile-expandable_section">
            <div className="assets-slider_mobile-panel">
              <div ref={contentRef} className="assets-slider_content">
                {sliderItems}
              </div>
              <div
                className="assets-slider_mobile-toggle"
                role="button"
                tabIndex={0}
                onClick={() => setIsMobileExpanded(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsMobileExpanded(false);
                  }
                }}
              >
                <ArrowDownSvg className="assets-slider_mobile-chevron" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="assets-slider">
      <button
        className="assets-slider_btn assets-slider_btn-prev"
        type="button"
        onClick={onPrev}
      >
        <ArrowLeftSvg />
      </button>
      <div ref={contentRef} className="assets-slider_content">
        {sliderItems}
      </div>
      <button
        className="assets-slider_btn assets-slider_btn-next"
        type="button"
        onClick={onNext}
      >
        <ArrowRightSvg />
      </button>
    </div>
  );
}
